import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Lock, Check, Loader2, Users } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function RolesPermissions() {
  const { isAdmin } = useAuth();
  const [roles, setRoles] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [confirmDeleteRole, setConfirmDeleteRole] = useState(null);

  const load = () => api.get("/roles").then((r) => {
    setRoles(r.data.items);
    setSelected((cur) => cur ? r.data.items.find((x) => x.role === cur.role) || r.data.items[0] : r.data.items[0]);
  }).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/menu-items").then((r) => setMenuItems(r.data.items)).catch(() => {});
    load();
  }, [isAdmin]);

  useEffect(() => { if (selected) setDraft(selected.menus || []); }, [selected]);

  if (!isAdmin)
    return (
      <div className="p-8 max-w-lg mx-auto text-center">
        <Lock className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
        <h2 className="font-display text-xl font-semibold">Access restricted</h2>
        <p className="text-sm text-muted-foreground mt-1">Only Super Admin and Owner can manage roles and permissions.</p>
      </div>
    );

  const isSystem = selected?.is_system;
  const toggle = (key) => setDraft((d) => d.includes(key) ? d.filter((k) => k !== key) : [...d, key]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/roles/${encodeURIComponent(selected.role)}`, { menus: draft });
      toast.success("Permissions updated");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const createRole = async () => {
    try {
      await api.post("/roles", { role: newRole, menus: ["/"] });
      toast.success("Role created");
      setCreateOpen(false); setNewRole(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteRole = async (role) => {
    setConfirmDeleteRole(null);
    try { await api.delete(`/roles/${encodeURIComponent(role)}`); toast.success("Role deleted"); setSelected(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto">
      <PageHeader title="Access Control" subtitle="Manage roles and control which menu items each role can access"
        actions={<Button onClick={() => setCreateOpen(true)} data-testid="new-role-btn"><Plus className="h-4 w-4 mr-1.5" />New Role</Button>} />

      <div className="grid lg:grid-cols-[300px_1fr] gap-6">
        {/* Roles list */}
        <div className="space-y-2">
          {roles.map((r) => (
            <button key={r.role} onClick={() => setSelected(r)} data-testid={`role-item-${r.role.replace(/\s+/g, "-").toLowerCase()}`}
              className={`w-full text-left rounded-lg border p-3.5 transition-colors ${selected?.role === r.role ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-accent/50"}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm flex items-center gap-2">
                  {r.is_system && <ShieldCheck className="h-3.5 w-3.5 text-primary" />}{r.role}
                </span>
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" />{r.user_count}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{r.is_system ? "Full access · system role" : `${r.menus.length} menus`}</div>
            </button>
          ))}
        </div>

        {/* Permission matrix */}
        <div className="rounded-lg border border-border bg-card">
          {!selected ? <div className="p-8 text-center text-muted-foreground">Select a role</div> : (
            <>
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div>
                  <div className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
                    {selected.is_system && <ShieldCheck className="h-4 w-4 text-primary" />}{selected.role}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{isSystem ? "System roles always have full access" : "Toggle the menu items this role can see"}</div>
                </div>
                {!isSystem && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="text-destructive" onClick={() => setConfirmDeleteRole(selected.role)} data-testid="delete-role-btn"><Trash2 className="h-4 w-4" /></Button>
                    <Button size="sm" onClick={save} disabled={saving} data-testid="save-permissions-btn">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}Save</Button>
                  </div>
                )}
              </div>

              <div className="p-6">
                {isSystem ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Lock className="h-4 w-4" />This role can access every menu item and manage permissions.</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-2">
                    {menuItems.map((m) => {
                      const on = draft.includes(m.key);
                      const locked = m.key === "/";
                      return (
                        <button key={m.key} disabled={locked} onClick={() => toggle(m.key)} data-testid={`menu-toggle-${m.key}`}
                          className={`flex items-center justify-between rounded-md border px-3.5 py-2.5 text-sm transition-colors ${on ? "border-primary/50 bg-primary/5" : "border-border bg-secondary/20 hover:bg-accent/40"} ${locked ? "opacity-70 cursor-not-allowed" : ""}`}>
                          <span className="font-medium">{m.label}</span>
                          <span className={`h-4 w-4 rounded flex items-center justify-center ${on ? "bg-primary text-primary-foreground" : "border border-border"}`}>
                            {on && <Check className="h-3 w-3" />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display tracking-tight">Create Role</DialogTitle><DialogDescription>Add a new custom role, then choose which menu items it can access.</DialogDescription></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Role name</Label>
            <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="e.g. Regional Sales Lead" data-testid="new-role-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createRole} disabled={!newRole.trim()} data-testid="create-role-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDeleteRole} onOpenChange={(o) => !o && setConfirmDeleteRole(null)}>
        <AlertDialogContent data-testid="confirm-delete-role-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role “{confirmDeleteRole}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the role and its menu permissions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-role-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteRole(confirmDeleteRole)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-role-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
